import React, { useState, useRef, useEffect } from 'react';
import { FormControl, Slider, FormHelperText } from '@mui/material';
import PropTypes from 'prop-types';
import { TextField } from '@totalsoft/rocket-ui';
import throttle from 'lodash.throttle';

const styles = {
  ['&.MuiSlider-root']: {
    top: '-1px',
    margin: 0,
    ['& .MuiSlider-markLabel']: {
      top: '20px',
      fontSize: '14px',
      lineHeight: '1.5em'
    }
  }
};

const RangeSlider = ({
  label,
  value,
  min,
  max,
  step,
  onChange,
  disabled,
  showSliderLimits,
  decimalScale,
  fullWidth,
  required,
  error,
  helperText
}) => {
  const [textMinValue, setTextMinValue] = useState(value[0]);
  const [textMaxValue, setTextMaxValue] = useState(value[1]);
  const [minValue, maxValue] = [...value];

  const onMinTextChanged = value => {
    if (value !== textMinValue) setTextMinValue(value);
  };

  const onMaxTextChanged = value => {
    if (value !== textMaxValue) setTextMaxValue(value);
  };

  const delayedMinValueChange = useRef(throttle(q => onMinTextChanged(q), 500)).current;
  const delayedMaxValueChange = useRef(throttle(q => onMaxTextChanged(q), 500)).current;

  useEffect(() => {
    delayedMinValueChange(minValue);
  }, [minValue, delayedMinValueChange]);

  useEffect(() => {
    delayedMaxValueChange(maxValue);
  }, [maxValue, delayedMaxValueChange]);

  const handleMinValueBlur = function () {
    if (textMinValue) {
      if (textMinValue <= min) {
        onChange(null, [min, value[1]]);
      } else if (textMinValue >= max) {
        onChange(null, [value[1], max]);
      } else {
        onChange(null, [textMinValue, value[1]]);
      }
    } else {
      onChange(null, [min, value[1]]);
    }
  };

  const handleMaxValueBlur = function () {
    if (textMaxValue) {
      if (textMaxValue <= min) {
        onChange(null, [min, value[0]]);
      } else if (textMaxValue >= max) {
        onChange(null, [value[0], max]);
      } else {
        onChange(null, [value[0], textMaxValue]);
      }
    } else {
      onChange(null, [value[0], max]);
    }
  };

  return (
    <FormControl fullWidth={fullWidth} error={error} required={required}>
      <TextField
        label={label}
        isNumeric={true}
        fullWidth={fullWidth}
        required={required}
        value={textMinValue}
        onBlur={handleMinValueBlur}
        onChange={onMinTextChanged}
        InputProps={{ disableUnderline: true }}
        inputProps={{ decimalScale: decimalScale, style: { textAlign: 'left' } }}
        disabled={disabled}
        sx={{ margin: '-14px 0' }}
      />

      <TextField
        isNumeric={true}
        fullWidth={fullWidth}
        required={required}
        value={textMaxValue}
        onBlur={handleMaxValueBlur}
        onChange={onMaxTextChanged}
        InputProps={{ disableUnderline: true }}
        inputProps={{ decimalScale: decimalScale, style: { textAlign: 'right' } }}
        disabled={disabled}
        sx={{ margin: '-14px 0' }}
      />
      <Slider
        sx={styles}
        value={value}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={onChange}
        size='small'
        marks={
          showSliderLimits && [
            { value: min, label: min.toFixed(decimalScale).toString() },
            { value: max, label: max.toFixed(decimalScale).toString() }
          ]
        }
      />
      <FormHelperText error={error}>{helperText}</FormHelperText>
    </FormControl>
  );
};

RangeSlider.propTypes = {
  label: PropTypes.string,
  value: PropTypes.array,
  onChange: PropTypes.func,
  fullWidth: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  showSliderLimits: PropTypes.bool,
  decimalScale: PropTypes.number
};

export default RangeSlider;
